import React, { useEffect, useRef, useState } from 'react';
import './_CashierHeader.sass';
import { shallowEqual, useSelector } from 'react-redux';
import ProfileModal from 'shared-components/dependent-components/modals/profile-modal/profile-modal.js';
import TransactionsModal from 'shared-components/dependent-components/modals/transactions-modal/transactions-modal';
import logoVirtual from 'shared-components/dependent-components/header/img/logo.svg';
import { config as appConfig } from 'config/config';
import { withRouter } from 'react-router';
import compose from 'utils/helper-functions/compose';
import { withTemplate } from 'shared-components/hoc-components/hoc-context/with-template';
import { withI18N } from 'shared-components/hoc-components';
import { isMobile } from 'utils/detect-devices/is-mobile';
import MyInfo from 'shared-components/dependent-components/header/my-info/my-info';
import Greeting from 'shared-components/dependent-components/header/greeting/greeting';
import { withAdr } from 'shared-components/hoc-components/with-adr';
import { withCurrentPage } from 'shared-components/hoc-components/with-current-page';
import { ArrowDown } from '@cashier-project/assets/images/arrow-down';
import { eNumber } from 'utils/round-functions/e-number';
import { clientClickEvent } from 'utils/custom-cashier-events/client-click-event';
import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import BackToCardMethod from 'shared-components/dependent-components/back-to-card-method/back-to-card-method';
import useBackToCardButton from 'shared-components/dependent-components/back-to-card-method/use-back-tocard-button';
import IconUser from '@pano-project/assets/icons/IconUser';
import { formatBalance } from '@pano-project/utils/formatBalance';
import TransactionTabsHeader from '@pano-project/components/TransactionTabsHeader/TransactionTabsHeader';
import HeaderIconButton from '@pano-project/components/HeaderButtons/HeaderIconButton';
import { debounce } from 'utils/helper-functions/debounce';
import { usePanoramicHeaderBack } from '@pano-project/features/panoramic-billing-page/hooks/usePanoramicHeaderBack';
import { useCashierHeaderEvents } from '@pano-project/hooks/useCashierHeaderEvents';
import { useHeaderState, CashierHeaderConfig } from './HeaderContext';
import { resolveHeaderButtons } from '@pano-project/variables/header-buttons';

const logo = appConfig.logo;

interface CashierHeaderProps {
  history?: any;
  template?: any;
  lang?: (key: string) => string;
  langBack?: (key: string) => string;
  s?: (key: string) => string;
  currentPage?: string;
  config?: CashierHeaderConfig;
}

const CashierHeader = ({ history, template, lang, langBack, s, currentPage, config: configProp }: CashierHeaderProps) => {
  const { goBackToCard } = useBackToCardButton();
  const { handleBack: handlePanoramicBack } = usePanoramicHeaderBack();
  const contextConfig = useHeaderState();

  const containerRef = useRef<HTMLElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const myInfoRef = useRef<HTMLDivElement | null>(null);
  const greetingRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isTwoRowsHeader, setIsTwoRowsHeader] = useState(false);
  const [greetingMaxWidth, setGreetingMaxWidth] = useState<number | null>(null);
  const [billingModal, setBillingModal] = useState(false);
  const [transactionsModal, setTransactionsModal] = useState(false);

  const {
    resultOfPay,
    type,
    session_type,
    base_amount,
    base_currency,
    show_back_to_card,
    balance,
    balanceCurrency,
    show_balance,
    header_style,
    showBackButtonSetting,
    showCloseButtonSetting,
    layout,
  } = useSelector((state: any) => {
    const profileInfo = state.profileInfo || {};
    const initTheme = state.initTheme;
    const cs = initTheme?.theme_editor_settings?.customization_settings;
    const profileBaseCurrency = profileInfo.base_currency;
    return {
      resultOfPay: state.resultOfPay,
      type: state.resultOfPay?.transaction_type || profileInfo.transaction_type,
      session_type: state.resultOfPay?.session_type || profileInfo.session_type,
      base_amount: profileInfo.base_amount,
      base_currency: profileBaseCurrency,
      show_back_to_card: state.cardToObSettings?.show_back_to_card,
      balance: initTheme?.sessionInformation?.balance,
      balanceCurrency: initTheme?.sessionInformation?.currency || profileBaseCurrency,
      show_balance: cs?.show_balance,
      header_style: cs?.header_style,
      showBackButtonSetting: cs?.show_back_button,
      showCloseButtonSetting: cs?.show_close_button,
      layout: cs?.layout,
    };
  }, shallowEqual);

  const { showBackButton, showCloseButton, showAnyHeaderButton } = resolveHeaderButtons({
    showBackButton: showBackButtonSetting,
    showCloseButton: showCloseButtonSetting,
    layout,
    headerStyle: header_style,
  });

  const isModernHeader = header_style === 'modern';
  const isAmountFirst = layout === 'amount-first';

  const { notifyClose } = useCashierHeaderEvents();

  const reducedHeaderModeByPage: Record<string, 'close-only' | 'back-and-close'> = {
    resultPage: 'close-only',
    errorPage: 'back-and-close',
    redirectPage: 'back-and-close',
    redirectEmbeddedPage: 'back-and-close',
  };
  const reducedHeaderMode = showAnyHeaderButton
    ? reducedHeaderModeByPage[currentPage as string]
    : undefined;

  const formattedBalance =
    show_balance && balance ? formatBalance(balance, balanceCurrency, undefined) || undefined : undefined;

  let pageTitle = '';
  switch (type) {
    case 'pay-in':
      pageTitle = base_amount
        ? `${lang!('pay')} ${eNumber(base_amount)} ${base_currency}`
        : `${lang!('deposit')}`;
      break;
    case 'pay-out':
      pageTitle = base_amount
        ? `${lang!('withdraw')} ${eNumber(base_amount)} ${base_currency}`
        : `${lang!('withdrawal')}`;
      break;
    default:
      pageTitle = '';
  }

  const handleBack = () => {
    clientClickEvent('back_button');
    history?.goBack();
  };

  const headerLogo = (
    <div className={`${s!('header-logo')}`} ref={headerRef}>
      {!isPanoramicTheme &&
      currentPage === 'paymentPage' &&
      !(['slider', 'beside'].includes(template?.sliderMethods) && !isMobile) ? (
        <div
          className={`${s!('back')}`}
          onClick={() => {
            if (show_back_to_card) {
              goBackToCard({ callback: handleBack });
            } else {
              handleBack();
            }
          }}
        >
          <span className={`${s!('arrow-wrapper')}`}>
            <ArrowDown />
          </span>
          <span>{lang!('back')}</span>
        </div>
      ) : (
        <div className={`${s!('logo-container')}`}>
          {logo ? <img alt={''} src={session_type === 'vt' ? logoVirtual : logo} /> : ''}
          {show_back_to_card && isPanoramicTheme ? (
            <BackToCardMethod />
          ) : (
            <div className={`${s!('main-method')}`}>{pageTitle}</div>
          )}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (!showAnyHeaderButton) return undefined;

    const offset = 32;
    const containerElement = containerRef?.current;
    const backButtonElement = backButtonRef?.current;
    const headerElement = headerRef?.current;
    const myInfoElement = myInfoRef?.current;
    const greetingElement = greetingRef?.current;
    const closeButtonElement = closeButtonRef?.current;

    const checkElementsWidth = debounce(() => {
      const containerWidth = containerElement?.offsetWidth || 0;
      const backButtonWidth = backButtonElement?.offsetWidth || 0;
      const headerWidth = headerElement?.offsetWidth || 0;
      const myInfoWidth = myInfoElement?.offsetWidth || 0;
      const greetingWidth = greetingElement?.offsetWidth || 0;
      const closeButtonWidth = closeButtonElement?.offsetWidth || 0;

      const contentWidth =
        backButtonWidth + backButtonWidth + headerWidth + myInfoWidth + greetingWidth + closeButtonWidth;

      setIsTwoRowsHeader(!!containerWidth && containerWidth < contentWidth + offset);
      setGreetingMaxWidth(
        containerWidth -
          (headerWidth + myInfoWidth + (greetingRef.current?.firstChild as HTMLElement)?.offsetWidth + offset),
      );
    }, 50);

    const resizeObserver = new ResizeObserver(() => {
      checkElementsWidth();
    });

    containerElement && resizeObserver.observe(containerElement);
    backButtonElement && resizeObserver.observe(backButtonElement);
    headerElement && resizeObserver.observe(headerElement);
    myInfoElement && resizeObserver.observe(myInfoElement);
    greetingElement && resizeObserver.observe(greetingElement);
    closeButtonElement && resizeObserver.observe(closeButtonElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isTwoRowsHeader, showAnyHeaderButton]);

  const handleBackButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handlePanoramicBack(currentPage === 'redirectEmbeddedPage');
  };

  const handleCloseButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    notifyClose();
  };

  const renderBackButton = (onClick: (e: React.MouseEvent) => void = handleBackButtonClick) => (
    <HeaderIconButton
      variant="back"
      buttonRef={backButtonRef}
      onClick={onClick}
      className="trx-tabs__button _old-header-variant"
    />
  );

  const renderCloseButton = () => (
    <HeaderIconButton
      variant="close"
      buttonRef={closeButtonRef}
      onClick={handleCloseButtonClick}
      className="trx-tabs__button _old-header-variant"
    />
  );

  const renderTopBar = ({
    showBack,
    showClose,
    onBack,
    onClose,
    closeOnly = false,
    showErrorInfo = false,
    backDisabled = false,
    extraClassName = '',
  }: {
    showBack: boolean;
    showClose: boolean;
    onBack?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    closeOnly?: boolean;
    showErrorInfo?: boolean;
    backDisabled?: boolean;
    extraClassName?: string;
  }) => (
    <header
      ref={containerRef}
      className={`${s!('main-header')} _show-hide-and-back-buttons ${
        session_type === 'vt' ? s!('header-virtual_terminal') : ''
      } ${extraClassName}`}
    >
      <div
        className={`trx-tabs main-header__tabs ${
          closeOnly ? 'main-header__tabs--close-only' : 'main-header__tabs--buttons-only'
        }`}
      >
        <div className="trx-tabs__list">
          {showBack && (
            <HeaderIconButton
              variant="back"
              className="trx-tabs__button"
              buttonRef={backButtonRef}
              onClick={onBack}
              disabled={backDisabled}
              ariaLabel={(langBack ?? lang)?.('back')}
            />
          )}
          {showErrorInfo && (
            <div className="main-header__error-info">
              {logo ? <img alt="" src={session_type === 'vt' ? logoVirtual : logo} /> : ''}
              <span className="main-header__base-currency">{base_currency}</span>
              {formattedBalance && <span className="main-header__balance">{formattedBalance}</span>}
            </div>
          )}
          {showClose && (
            <HeaderIconButton
              variant="close"
              className="trx-tabs__button"
              buttonRef={closeButtonRef}
              onClick={onClose}
            />
          )}
        </div>
      </div>
    </header>
  );

  if (configProp || contextConfig) {
    const cfg = (configProp ?? contextConfig) as CashierHeaderConfig;

    if (cfg.variant === 'none') return null;

    if (cfg.variant === 'tabs') {
      return (
        <TransactionTabsHeader
          transactionType={cfg.transactionType as string}
          lang={(langBack ?? lang) as (key: string) => string}
          activeTab={cfg.activeTab}
          onTabClick={cfg.onTabClick}
          withPending={cfg.withPending}
          balance={cfg.balance}
        />
      );
    }

    if (cfg.variant === 'top-bar') {
      const closeVisible = cfg.showClose ?? showCloseButton;
      const backVisible = cfg.showBack ?? true;
      const onClose =
        cfg.onClose ??
        ((e: React.MouseEvent) => {
          e.preventDefault();
          notifyClose();
        });

      return renderTopBar({
        showBack: backVisible,
        showClose: closeVisible,
        onBack: cfg.onBack,
        onClose,
        closeOnly: !backVisible,
        backDisabled: cfg.backDisabled,
        extraClassName: cfg.className ?? '',
      });
    }

    if (cfg.variant === 'title-row') {
      const closeVisible = cfg.showClose ?? showCloseButton;
      const backVisible = cfg.showBack ?? true;
      const onClose =
        cfg.onClose ??
        ((e: React.MouseEvent) => {
          e.preventDefault();
          notifyClose();
        });

      if (cfg.titleLayout === 'stacked') {
        return (
          <>
            <div className={cfg.className ?? 'cashier-header__actions'}>
              {backVisible && (
                <HeaderIconButton
                  variant="back"
                  onClick={cfg.onBack}
                  ariaLabel={(langBack ?? lang)?.('back')}
                />
              )}
              {closeVisible && <HeaderIconButton variant="close" onClick={onClose} />}
            </div>
            <h1 className={cfg.titleClassName ?? 'cashier-header__stacked-title'}>{cfg.title}</h1>
          </>
        );
      }

      return (
        <div className={cfg.className ?? 'cashier-header cashier-header--title-row'}>
          {backVisible && (
            <HeaderIconButton variant="back" onClick={cfg.onBack} disabled={cfg.backDisabled} />
          )}
          <span
            className={`${cfg.titleClassName ?? 'cashier-header__title'}${
              cfg.showStickyTitle ? ' _visible' : ''
            }`}
          >
            {cfg.title}
          </span>
          {closeVisible && <HeaderIconButton variant="close" onClick={onClose} />}
        </div>
      );
    }
  }

  if (resultOfPay && currentPage !== 'errorPage' && !isPanoramicTheme) {
    return (
      <div
        className={`${s!('main-header')} ${session_type === 'vt' ? s!('header-virtual_terminal') : ''}`}
      >
        {headerLogo}
      </div>
    );
  }

  const handleMinimalBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handlePanoramicBack(true);
  };

  if (reducedHeaderMode) {
    const backAllowedOnPage = reducedHeaderMode !== 'close-only';
    const showBack = backAllowedOnPage && showBackButton;

    return renderTopBar({
      showBack,
      showClose: showCloseButton,
      onBack: handleMinimalBackClick,
      onClose: handleCloseButtonClick,
      closeOnly: !showBack,
      showErrorInfo: currentPage === 'errorPage',
      extraClassName: 'main-header--reduced',
    });
  }

  return (
    <>
      {billingModal && <ProfileModal close={() => setBillingModal(false)} />}
      {transactionsModal && <TransactionsModal close={() => setTransactionsModal(false)} />}

      {currentPage !== 'errorPage' && (
        <>
          {show_back_to_card && !isPanoramicTheme && <BackToCardMethod />}

          {isTwoRowsHeader && (
            <div className="main-header__buttons-wrapper">
              {showBackButton && renderBackButton()}
              {showCloseButton && renderCloseButton()}
            </div>
          )}

          <header
            ref={containerRef}
            className={`${s!('main-header')} ${
              session_type === 'vt' ? s!('header-virtual_terminal') : ''
            } ${showAnyHeaderButton ? '_show-hide-and-back-buttons' : ''}`}
          >
            {isPanoramicTheme && (isModernHeader || isAmountFirst) ? (
              <TransactionTabsHeader
                transactionType={type}
                lang={lang as (key: string) => string}
                onBack={() => handlePanoramicBack(currentPage === 'redirectEmbeddedPage')}
                balanceTabClassName={type}
                balance={formattedBalance}
                {...(!isAmountFirst && {
                  isAmountFirstLayout: false,
                  className: 'main-header__tabs',
                  balanceSuffix:
                    type === 'pay-out' && session_type !== 'vt' && appConfig.withdrawRequests ? (
                      <MyInfo
                        setBillingModal={setBillingModal}
                        setTransactionsModal={setTransactionsModal}
                        triggerElement={
                          <button className={'user-info-btn'} type={'button'}>
                            <IconUser />
                          </button>
                        }
                      />
                    ) : undefined,
                })}
              />
            ) : (
              <>
                {showBackButton && !isTwoRowsHeader && renderBackButton()}
                {headerLogo}

                <span className="main-header__base-currency">{base_currency}</span>

                {type === 'pay-out' && session_type !== 'vt' && appConfig.withdrawRequests ? (
                  <MyInfo
                    myInfoRef={myInfoRef}
                    setBillingModal={setBillingModal}
                    setTransactionsModal={setTransactionsModal}
                  />
                ) : (
                  <Greeting
                    greetingMaxWidth={greetingMaxWidth}
                    greetingRef={greetingRef}
                    setBillingModal={setBillingModal}
                  />
                )}
                {showCloseButton && !isTwoRowsHeader && renderCloseButton()}
              </>
            )}
          </header>
        </>
      )}
    </>
  );
};

export default compose(withRouter, withTemplate, withI18N, withAdr, withCurrentPage)(CashierHeader);
